var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import exif from 'exif-js';
import { apiCreateDirectStorageUpload, apiCreateFileUpload, apiCreateSecureFileUpload, apiGetFileStatusCheck, } from '../actions/FileActions';
import { addToastId, deleteToastId } from '../actions/ToastActions';
import { notifyUploading, updateNotifyUploading, wrapDispatch } from './action';
import { getUploadFileErrorText } from './validate';
export const getFileName = (obj) => {
    if (obj instanceof File) {
        return obj.name;
    }
    if (typeof obj === 'string') {
        return obj;
    }
    return '';
};
/**
 * file uploadの実行
 * @deprecated TS化済 src/utils/file/uploadFile.ts
 * @param options
 * @returns {Promise<unknown>}
 */
export const uploadFilePromise = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const { dispatch, file, showProgress, unmountRef } = options;
    // ファイルの値を整形
    const formData = new FormData();
    formData.append('upload_file', file, file.name);
    // アップロードプログレス表示制御用のID
    let toastId = null;
    // アップロード実行
    try {
        const { payload } = yield wrapDispatch(dispatch, apiCreateFileUpload(formData, {
            wrapOnUploadProgress: (progress) => {
                // アップロードプログレスバー非表示、もしくはunmount状態であれば何もしない
                if (!showProgress || (unmountRef && unmountRef.current)) {
                    return;
                }
                if (toastId === null) {
                    // アップロードプログレスバーを表示
                    toastId = notifyUploading(progress);
                    dispatch(addToastId(toastId));
                }
                else {
                    // 表示中のアップロードプログレスバーを更新
                    updateNotifyUploading(toastId, progress);
                }
            },
        }));
        return payload;
    }
    finally {
        // アップロード完了
        if (toastId && unmountRef && !unmountRef.current) {
            updateNotifyUploading(toastId, 100);
            dispatch(deleteToastId(toastId));
        }
    }
});
/**
 * file uploadの実行とIDの取得
 * @param options
 * @returns {Promise<number>}
 */
export const uploadFilePromiseAndGetTemporaryId = (options) => __awaiter(void 0, void 0, void 0, function* () {
    const result = yield uploadFilePromise(options);
    return result.id;
});
/**
 * secureなfile uploadの実行（複数選択対応）
 * @param dispatch
 * @param unmountRef
 * @param file
 * @param meta
 * @param onUploadProgress
 * @returns {Promise<number | null>}
 */
export const uploadSecureFile = (dispatch, unmountRef, file, meta, onUploadProgress) => __awaiter(void 0, void 0, void 0, function* () {
    // 秘匿用の仮bucketにアップロード
    const action = apiCreateSecureFileUpload(file.name, meta);
    const response = yield wrapDispatch(dispatch, action);
    const { payload: { contentType, id, url }, } = response;
    if (unmountRef.current) {
        return null;
    }
    // s3への直接アップロード
    const uploadAction = apiCreateDirectStorageUpload(url, file, contentType, Object.assign(Object.assign({}, meta), { wrapOnUploadProgress: onUploadProgress }));
    yield wrapDispatch(dispatch, uploadAction);
    return id;
});
/**
 * IDの取得（複数選択対応）
 * @param dispatch
 * @param unmountRef
 * @param id
 * @param errorCallback
 * @returns {Promise<number| null>}
 */
export const getTemporaryId = (dispatch, unmountRef, id, errorCallback) => __awaiter(void 0, void 0, void 0, function* () {
    if (unmountRef.current) {
        return null;
    }
    // 処理完了のポーリング
    try {
        yield new Promise((resolve, reject) => {
            let running = false;
            const interval = setInterval(() => __awaiter(void 0, void 0, void 0, function* () {
                if (running) {
                    return;
                }
                if (unmountRef.current) {
                    resolve(null);
                    clearInterval(interval);
                    return;
                }
                try {
                    running = true;
                    const statusAction = apiGetFileStatusCheck(id);
                    const statusResponse = yield wrapDispatch(dispatch, statusAction);
                    const { payload: { secureCompleteAt, secureFailedType }, } = statusResponse;
                    if (secureCompleteAt) {
                        resolve();
                        clearInterval(interval);
                    }
                    else if (secureFailedType) {
                        reject(secureFailedType);
                        clearInterval(interval);
                    }
                }
                finally {
                    running = false;
                }
            }), 2000);
        });
    }
    catch (e) {
        const errorText = getUploadFileErrorText(e);
        if (errorText) {
            if (!unmountRef.current) {
                errorCallback(errorText);
            }
            return null;
        }
        throw e;
    }
    return id;
});
/**
 * secureなfile uploadの実行とIDの取得（複数選択非対応）
 * @param dispatch
 * @param unmountRef
 * @param file
 * @param meta
 * @param errorCallback
 * @param onUploadProgress
 * @returns {Promise<null|*>}
 */
export const uploadSecureFilePromiseAndGetTemporaryId = (dispatch, unmountRef, file, meta, errorCallback, onUploadProgress) => __awaiter(void 0, void 0, void 0, function* () {
    // secureなfile uploadの実行
    const secureFileId = yield uploadSecureFile(dispatch, unmountRef, file, meta, onUploadProgress);
    // 処理完了のポーリング
    const temporaryId = yield getTemporaryId(dispatch, unmountRef, secureFileId, errorCallback);
    return temporaryId;
});
/**
 * ファイルの読み込みのヘルパ
 * @param file
 * @returns {Promise<*>}
 */
export const fileLoad = (file) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = ({ target }) => {
            resolve(target);
        };
        reader.readAsDataURL(file);
    });
    return response.result;
});
/**
 * 傾いた画像を補正して返却する
 * @param dataUrl
 * @returns {Promise<*>}
 */
export const fixRotate = (dataUrl) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const file = { src: dataUrl };
        yield new Promise((resolve) => {
            exif.getData(file, resolve);
        });
        const { Orientation: orientation } = file.exifdata;
        if (!orientation || orientation === 0) {
            return dataUrl;
        }
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        img.src = dataUrl;
        img.crossOrigin = 'anonymous';
        const result = yield new Promise((resolve, reject) => {
            img.onload = () => {
                try {
                    switch (orientation) {
                        case 3: {
                            // // 画像が１８０度回転している時
                            canvas.width = img.width;
                            canvas.height = img.height;
                            ctx.rotate(Math.PI);
                            ctx.drawImage(img, -img.width, -img.height);
                            ctx.rotate(-Math.PI);
                            break;
                        }
                        case 6: {
                            // 画像が時計回りに９０度回っている時
                            canvas.width = img.height;
                            canvas.height = img.width;
                            ctx.rotate(Math.PI * 0.5);
                            ctx.drawImage(img, 0, -img.height);
                            ctx.rotate(-Math.PI * 0.5);
                            break;
                        }
                        case 8: {
                            // 画像が反時計回りに９０度回っている時
                            canvas.width = img.height;
                            canvas.height = img.width;
                            ctx.rotate(-Math.PI * 0.5);
                            ctx.drawImage(img, -img.width, 0);
                            ctx.rotate(Math.PI * 0.5);
                            break;
                        }
                        default: {
                            canvas.width = img.width;
                            canvas.height = img.height;
                            ctx.drawImage(img, 0, 0);
                        }
                    }
                    resolve(canvas.toDataURL());
                }
                catch (e) {
                    reject(e);
                }
            };
        });
        return result;
    }
    catch (e) {
        // 上手くいかなかった場合は元の画像を表示しておこう
        return dataUrl;
    }
});
/**
 * ファイルの読み込みのヘルパ
 * @param file
 * @returns {Promise<*>}
 */
export const fileLoadBuffer = (file) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = ({ target }) => {
            resolve(target);
        };
        reader.readAsArrayBuffer(file);
    });
    return response.result;
});
/**
 * ファイルを新しいタブで開く
 * @deprecated TS化済 src/utils/file/open.ts
 * @param file
 */
export const openFileNewTab = (file) => {
    if (!file) {
        return;
    }
    if (file instanceof File) {
        const blob = new Blob([file], { type: file.type });
        if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, file.name);
        }
        else {
            const fileURL = window.URL.createObjectURL(blob);
            window.open(fileURL, '_blank');
            window.URL.revokeObjectURL(fileURL);
        }
    }
    else if (typeof file === 'string') {
        window.open(file, '_blank');
    }
};
/**
 * URLを新しいタブで開く
 * @param fileUrl
 */
export const openUrlNewTab = (fileUrl) => {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.target = '_blank';
    a.click();
};
